import {
  SUMMARY_METRIC_COST_PER_HA,
  SUMMARY_METRIC_QUANTITY,
  SUMMARY_METRIC_TOTAL_COST,
} from 'constants/uiConstants';
import type { SummaryDataMetric } from '../../../../../redux/slices/InsightsSlice';
import type {
  SummaryReportRow,
  SummaryReportSideBySideRow,
} from './computeSummaryReport';
import type { SummaryDataSide } from './dataBasis';

/** The metrics an Input summaries chart can plot — Total Area isn't tracked per product row. */
export const CHARTABLE_SUMMARY_METRICS = new Set<SummaryDataMetric>([
  SUMMARY_METRIC_QUANTITY,
  SUMMARY_METRIC_TOTAL_COST,
  SUMMARY_METRIC_COST_PER_HA,
]);

/** Group key for $ metrics — every bar shares one axis. */
export const CHART_GROUP_COST = 'cost';

export interface InputSummaryChartBar {
  key: string;
  label: string;
  /** Value per side — a side is absent when it has nothing to plot for this product. */
  values: Partial<Record<SummaryDataSide, number>>;
}

/**
 * One axis of bars. $ metrics produce a single group; Quantity produces one
 * group per base unit (mass in kg, liquid in L, …) so different units are
 * never drawn against the same scale.
 */
export interface InputSummaryChartGroup {
  /** CHART_GROUP_COST, or the base quantity unit ('kg', 'L', …). */
  key: string;
  bars: InputSummaryChartBar[];
  /** Largest value across every bar and side in this group — the axis maximum. */
  max: number;
}

/** Mass first, then liquid, then any other unit alphabetically. */
const UNIT_ORDER: Record<string, number> = { kg: 0, L: 1 };

const valueFor = (
  row: SummaryReportRow,
  metric: SummaryDataMetric
): number | null => {
  if (metric === SUMMARY_METRIC_QUANTITY) return row.quantity;
  if (metric === SUMMARY_METRIC_TOTAL_COST) return row.cost;
  if (metric === SUMMARY_METRIC_COST_PER_HA) return row.costPerHa;
  return null;
};

const barLabel = (row: SummaryReportSideBySideRow): string =>
  row.group ? `${row.group} · ${row.label}` : row.label;

const barMax = (bar: InputSummaryChartBar): number =>
  Math.max(0, ...Object.values(bar.values).map((v) => v ?? 0));

/**
 * Chart data for one Input summaries section: horizontal bars per product,
 * largest first, with one value per side (Plan / Actual — never summed).
 * Values that are missing, zero or negative are left out; a product with
 * nothing to plot on any side is dropped. Returns [] for a metric that
 * can't be charted per product (Total Area).
 */
export const computeInputSummaryChart = (
  rows: SummaryReportSideBySideRow[],
  sides: SummaryDataSide[],
  metric: SummaryDataMetric
): InputSummaryChartGroup[] => {
  if (!CHARTABLE_SUMMARY_METRICS.has(metric)) return [];

  const groups = new Map<string, Map<string, InputSummaryChartBar>>();

  for (const row of rows) {
    for (const side of sides) {
      const sideRow = row.sides[side];
      if (!sideRow) continue;
      const value = valueFor(sideRow, metric);
      if (value === null || value <= 0) continue;

      const groupKey =
        metric === SUMMARY_METRIC_QUANTITY
          ? (sideRow.quantityUnit ?? '')
          : CHART_GROUP_COST;
      const bars = groups.get(groupKey) ?? new Map();
      const bar: InputSummaryChartBar = bars.get(row.key) ?? {
        key: row.key,
        label: barLabel(row),
        values: {},
      };
      bar.values[side] = value;
      bars.set(row.key, bar);
      groups.set(groupKey, bars);
    }
  }

  return Array.from(groups.entries())
    .map(([key, bars]) => {
      const sorted = Array.from(bars.values()).sort(
        (a, b) =>
          barMax(b) - barMax(a) ||
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
      );
      return {
        key,
        bars: sorted,
        max: sorted.length > 0 ? barMax(sorted[0]) : 0,
      };
    })
    .sort(
      (a, b) =>
        (UNIT_ORDER[a.key] ?? 2) - (UNIT_ORDER[b.key] ?? 2) ||
        a.key.localeCompare(b.key)
    );
};
